import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Card, Select, Space, Tag, Tooltip, Typography, message } from "antd";
import { DownloadOutlined, LockOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import { CopyCommand } from "../../../components/ui/common";
import {
  DOMAIN_ID,
  ORG_ID,
  REACT_APP_API,
  REACT_APP_AD_SERVICE_URL,
  REACT_APP_POLICY_SERVICE_URL,
} from "../../../constants";

const { Text, Title } = Typography;

const INSTALL_PS1_URL =
  "https://raw.githubusercontent.com/authnull0/windows-endpoint/main/ad-sensor/install.ps1";
const RUN_INSTALL_CMD = ".\\install.ps1";
const VERIFY_SERVICE_CMD = "Get-Service AuthnullDCSensor";

const overrideYamlField = (yaml, key, value) => {
  const re = new RegExp(`^${key}:.*$`, "m");
  const replacement = `${key}: "${value}"`;
  if (re.test(yaml)) return yaml.replace(re, replacement);
  return `${replacement}\n${yaml}`;
};

const LockedDomainDisplay = ({ value, options = [] }) => {
  const opt = options.find((o) => String(o.value) === String(value));
  const label = opt?.label || value || "—";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        height: 32,
        padding: "0 12px",
        background: "var(--an-surface)",
        border: "1px solid rgba(0,0,0,0.5)",
        borderRadius: 6,
        fontSize: 13,
        color: "var(--an-ink)",
        fontWeight: 500,
      }}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
      <Tooltip title="This sensor is being installed for the AD you just connected">
        <LockOutlined style={{ color: "var(--an-muted)", fontSize: 12 }} />
      </Tooltip>
    </div>
  );
};

const ENFORCEMENT_LABELS = {
  monitor: { text: "Monitor", color: "blue", hint: "Logs authentication events without blocking anything." },
  enforce: { text: "Enforce", color: "orange", hint: "Applies MFA verdicts. Users may be blocked." },
};

const FALLBACK_LABELS = {
  allow: { text: "Allow", color: "green", hint: "Logins continue without MFA during an outage." },
  deny: { text: "Deny", color: "red", hint: "Logins are refused during an outage." },
};

const DeploymentSetting = ({ label, spec }) => {
  if (!spec) return null;
  return (
    <div>
      <Text type="secondary" style={{ fontSize: 12.5, display: "block", marginBottom: 6 }}>
        {label}
      </Text>
      <Space size={8} align="center">
        <Tag color={spec.color} style={{ marginInlineEnd: 0 }}>{spec.text}</Tag>
        <Text type="secondary" style={{ fontSize: 12.5 }}>{spec.hint}</Text>
      </Space>
    </div>
  );
};

export default function GatewayInstall({
  defaultDomainId = "",
  domainOptions = [],
  enforcementMode = "",
  fallbackMode = "",
  lockDomain = false,
  doneLabel = "Done",
  doneTo = null,
}) {
  const navigate = useNavigate();
  const [notification, setNotification] = message.useMessage();

  const initialId = useMemo(() => {
    if (defaultDomainId) return String(defaultDomainId);
    if (domainOptions.length === 1) return String(domainOptions[0].id);
    return "";
  }, [defaultDomainId, domainOptions]);

  const [selectedId, setSelectedId] = useState(initialId);
  const [downloadingPs1, setDownloadingPs1] = useState(false);
  const [downloadingSensor, setDownloadingSensor] = useState(false);

  const effectiveId = selectedId || initialId;

  const triggerBlobDownload = async (url, filename) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  };

  const onDownloadInstallPs1 = async () => {
    setDownloadingPs1(true);
    try {
      await triggerBlobDownload(INSTALL_PS1_URL, "install.ps1");
    } catch (e) {
      notification.error({ content: "Could not download install.ps1. Check your network and try again." });
    } finally {
      setDownloadingPs1(false);
    }
  };

  const labelFor = (d) =>
    d.directoryName
      ? d.domain
        ? `${d.directoryName} (${d.domain})`
        : d.directoryName
      : d.domain || `Domain ${d.id}`;

  const selectOptions = domainOptions.map((d) => ({
    value: String(d.id),
    label: labelFor(d),
  }));

  const sensorUrl = effectiveId
    ? `${REACT_APP_API}/ad/DownloadSensorConfig?adId=${effectiveId}&orgId=${ORG_ID()}&tenantId=${DOMAIN_ID()}`
    : null;

  const onDownloadSensorYml = async () => {
    if (!sensorUrl) return;
    setDownloadingSensor(true);
    try {
      const res = await fetch(sensorUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      let yaml = await res.text();
      if (REACT_APP_AD_SERVICE_URL) {
        yaml = overrideYamlField(yaml, "ad_service_url", REACT_APP_AD_SERVICE_URL);
      }
      if (REACT_APP_POLICY_SERVICE_URL) {
        yaml = overrideYamlField(yaml, "policy_service_url", REACT_APP_POLICY_SERVICE_URL);
      }
      const blob = new Blob([yaml], { type: "application/x-yaml" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "sensor.yml";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
    } catch (e) {
      notification.error({ content: "Could not download sensor.yml. Check your network and try again." });
    } finally {
      setDownloadingSensor(false);
    }
  };

  const onDone = () => {
    // Fall back to the directory list. The old default pointed at
    // /pam/directory/gateways, which is not a registered route.
    navigate(doneTo || "/pam/directory");
  };

  return (
    <Space direction="vertical" size="large" className="w-full">
      {setNotification}
      <Box>
        <Title level={5}>Install DC Sensor</Title>
        <Text type="secondary" className="block mb-4">
          Install the AuthNull sensor on your Domain Controller. No additional configuration is required.
        </Text>

        <div style={{ maxWidth: 420, marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12.5, display: "block", marginBottom: 6 }}>
            Active Directory
          </Text>
          {lockDomain || domainOptions.length <= 1 ? (
            <LockedDomainDisplay value={effectiveId} options={selectOptions} />
          ) : (
            <Select
              value={effectiveId || undefined}
              onChange={(v) => setSelectedId(v)}
              placeholder="Select an AD"
              options={selectOptions}
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
            />
          )}
        </div>

        {(enforcementMode || fallbackMode) && (
          <Space direction="vertical" size={12} style={{ marginTop: 16 }}>
            <DeploymentSetting
              label="Enforcement Mode"
              spec={ENFORCEMENT_LABELS[enforcementMode]}
            />
            <DeploymentSetting
              label="Fallback if Authnull is unreachable"
              spec={FALLBACK_LABELS[fallbackMode]}
            />
          </Space>
        )}
      </Box>

      <Alert
        type="warning"
        showIcon
        message="A Domain Controller reboot is required"
        description={
          <span style={{ fontSize: 12.5 }}>
            <code>install.ps1</code> registers an LSA security package on the Domain Controller.
            NTLM enforcement does not take effect until the DC is rebooted. Plan a maintenance
            window before you run the installer.
          </span>
        }
      />

      <Card size="small">
        <Title level={5} className="!mb-3">Installation Steps</Title>

        <div className="mb-4">
          <Text strong className="block mb-1">Step 1 — Download files</Text>
          <Text type="secondary" className="block mb-2" style={{ fontSize: 12.5 }}>
            Download both files and place them in the same folder on the Domain Controller.
          </Text>
          <Space size="middle" wrap>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={onDownloadSensorYml}
              loading={downloadingSensor}
              disabled={!sensorUrl}
            >
              Download sensor.yml
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={onDownloadInstallPs1}
              loading={downloadingPs1}
            >
              Download install.ps1
            </Button>
          </Space>
        </div>

        <div className="mb-4">
          <Text strong className="block mb-1">Step 2 — Run as Administrator on the DC</Text>
          <Text type="secondary" className="block mb-2" style={{ fontSize: 12.5 }}>
            Open PowerShell as Administrator, <code>cd</code> into the folder, then run:
          </Text>
          <CopyCommand content={RUN_INSTALL_CMD} />
        </div>

        <div>
          <Text strong className="block mb-1">Step 3 — Verify</Text>
          <Text type="secondary" className="block mb-2" style={{ fontSize: 12.5 }}>
            Confirm the service is running before clicking Done:
          </Text>
          <CopyCommand content={VERIFY_SERVICE_CMD} />
        </div>
      </Card>

      <Box>
        <Space size="middle">
          <Button type="primary" onClick={onDone} disabled={!effectiveId}>
            {doneLabel}
          </Button>
        </Space>
      </Box>
    </Space>
  );
}
